(ns core.domain.system-of-units
  (:require [clojure.string :as str]
            [potpuri.core :as p]))

(defn make-conversions-map [& conversions]
  (reduce (fn [conversions conversion]
            (-> conversions
                (assoc (-> conversion :imperial first)
                       conversion)
                (assoc (-> conversion :metric first)
                       conversion)))
          {}
          conversions))

(def conversion-of-units-config
  "Under ':conversions' key we have {'base-unit' {'target-system' ['new-unit' 'conversion-function']}}
  e.g. to convert 1 sq. meter to sq. foot we use :m2 as 'base-unit' (:metric system is default),
  :imperial as 'target-system' (sq. foot is :imperial equivalent for sq. meter):
  (get-in conversions [:m2 :imperial])  we got [':ft2' 'm2->ft2']."
  {:units       {:m                               {:system-of-units :metric
                                                   :label           "m"}
                 :ft                              {:system-of-units :imperial
                                                   :label           "ft"}
                 :mm                              {:system-of-units :metric
                                                   :label           "mm"}
                 :inch                            {:system-of-units :imperial
                                                   :label           "inch"}
                 :m2                              {:system-of-units :metric
                                                   :label           "m²"}
                 :ft2                             {:system-of-units :imperial
                                                   :label           "sq.ft."}
                 :m3                              {:system-of-units :metric
                                                   :label           "m³"}
                 :ft3                             {:system-of-units :imperial
                                                   :label           "ft³"}
                 :kg                              {:system-of-units :metric
                                                   :label           "kg"}
                 :lb                              {:system-of-units :imperial
                                                   :label           "lb"}
                 :liter-per-second                {:system-of-units :metric
                                                   :label           "L/s"}
                 :ft3-per-minute                  {:system-of-units :imperial
                                                   :label           "cfm"}
                 :kw                              {:system-of-units :metric
                                                   :label           "kW"}
                 :btuh                            {:system-of-units :imperial
                                                   :label           "BTUH"}
                 :ventilation-multiplier-metric   {:system-of-units :metric
                                                   :label           ""}
                 :ventilation-multiplier-imperial {:system-of-units :imperial
                                                   :label           ""}
                 ;; thermal flow factor U
                 :btu-per-h-ft2-delta-f           {:system-of-units :imperial
                                                   :label           "Btu/(h·ft²·°F)"}
                 :w-per-kelvin-m2                 {:system-of-units :metric
                                                   :label           "W/(K·m²)"}}
   :conversions (make-conversions-map
                  {:imperial [:ft #(* % (/ 1 0.3048))]
                   :metric   [:m #(* % 0.3048)]}
                  {:imperial [:inch #(* % (/ 1 25.4))]
                   :metric   [:mm   #(* % 25.4)]}
                  {:imperial [:ft2 #(* % (/ 1 0.09290304))]
                   :metric   [:m2 #(* % 0.09290304)]}
                  {:imperial [:ft3 #(* % (/ 1 0.02831684659))]
                   :metric   [:m3 #(* % 0.02831684659)]}
                  {:imperial [:lb #(* % (/ 1 0.453592))]
                   :metric   [:kg #(* % 0.453592)]}
                  {:imperial [:ft3-per-minute #(* % (/ 1 0.4719474))]
                   :metric   [:liter-per-second #(* % 0.4719474)]}
                  {:imperial [:btuh #(* % (/ 1 0.00029307107))]
                   :metric   [:kw #(* % 0.00029307107)]}
                  {:imperial [:ventilation-multiplier-imperial #(* % 2.12)]
                   :metric   [:ventilation-multiplier-metric #(/ % 2.12)]}
                  {:imperial [:btu-per-h-ft2-delta-f #(* % (/ 1 0.1761103))]
                   :metric   [:w-per-kelvin-m2 #(* % 0.1761103)]})})


(def units
  (-> conversion-of-units-config :units))


(defn system-of-units->available-units [sou]
  (->> units
       (p/filter-vals #(-> % :system-of-units (= sou)))))


(def conversions
  (-> conversion-of-units-config :conversions))


(defn get-unit [base-unit system-of-units]
  (when system-of-units
    (some-> base-unit conversions system-of-units first)))


(defn unit-info [initial-unit system-of-units]
  (if-let [u (get-unit initial-unit system-of-units)]
    (-> units u)
    (-> units initial-unit)))


(defn round [precision d]
  (let [factor (Math/pow 10 precision)]
    (/ (Math/round (* d factor)) factor)))


(def default-precision 3)


(defn get-conversion-fn [base-unit system-of-units]
  (when (and system-of-units base-unit (not= system-of-units (-> units base-unit :system-of-units)))
    (some-> base-unit conversions system-of-units second)))


(defn convert-raw [base-unit system-of-units value]
  (if-let [conversion-fn (and value ;; Do not convert nil to 0
                              (get-conversion-fn base-unit system-of-units))]
    (conversion-fn value)
    value))


(defn convert-value
  ([base-unit system-of-units value]
   (convert-value base-unit system-of-units true value))
  ([base-unit system-of-units round? value]
   (if-let [conversion-fn (get-conversion-fn base-unit system-of-units)]
     (cond->> (conversion-fn value)
       round? (round default-precision))
     value)))


(defn default-system-of-units [available-unit-systems]
  (first available-unit-systems))


(defn unit-info-by-label [label]
  (let [label (when label
                (str/replace label #"2|3" {"2" "²" "3" "³"}))]
    (->> units
         (filter (fn [unit]
                   (when (= label (:label (val unit)))
                     unit)))
         first)))
